import {
  Add,
  Check,
  Lock,
  LockOpen,
  MoreVert,
  Person,
  PersonAdd,
  PersonOff,
  ReceiptLong,
  Scale,
  Search,
  Send,
  SwapHoriz,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';

import { ConfirmDestructiveDialog } from '../confirm-destructive-dialog';
import { useProducerDisplayName } from '../hooks/use-producer-display-name';
import {
  ErrorBar,
  LockedBar,
  LockedCutsheetActions,
  MissingCutsheetActions,
  ReminderActions,
  SuccessBar,
  UnlockedCutsheetActions,
  WarningBar,
} from './split-slot-parts';
import { CutsheetInfo } from './types';
import { getBuyerDisplayName } from './utils';

interface SplitSlotProps {
  label?: string;
  hangingWeight?: number;
  isHangingWeightCalculated?: boolean;
  existingBuyer?: CutsheetInfo;
  isSelf?: boolean;
  loading?: boolean;
  disabled?: boolean;
  cutsheetName?: string;
  isLocked?: boolean;
  processorName?: string;
  downloadingPdf?: boolean;
  canSendReminder?: boolean;
  reminderSentTo?: string | null;
  sendingReminder?: boolean;
  myselfLabel?: string;
  isProcessor?: boolean;
  togglingLock?: boolean;
  onSendReminder: () => Promise<void>;
  onSelectMyself: () => void;
  onSelectExisting: () => void;
  onSelectNew: () => void;
  onChangeBuyer: () => void;
  onLeaveUnassigned: () => void;
  onFillCutsheet: () => void;
  onEditCutsheet: () => void;
  onDownloadPdf: () => void;
  onToggleLock?: () => void;
  onAddYield?: () => void;
  yieldCount?: number;
  onGeneratePackingSlip?: () => void;
  generatingPackingSlip?: boolean;
  questionnaireOptions?: { label: string; value: string }[];
  currentQuestionnaireId?: string | null;
  onSelectQuestionnaire?: (id: string | null) => void;
  onPreviewCutsheet?: () => void;
  isQuestionnaireResponse?: boolean;
}

export function SplitSlot({
  label = undefined,
  hangingWeight = 0,
  isHangingWeightCalculated = false,
  existingBuyer = undefined,
  isSelf = false,
  loading = false,
  disabled = false,
  cutsheetName = undefined,
  isLocked = false,
  processorName = undefined,
  downloadingPdf = false,
  canSendReminder = false,
  reminderSentTo = null,
  sendingReminder = false,
  myselfLabel = 'Myself',
  isProcessor = false,
  togglingLock = false,
  onSendReminder,
  onSelectMyself,
  onSelectExisting,
  onSelectNew,
  onChangeBuyer,
  onLeaveUnassigned,
  onFillCutsheet,
  onEditCutsheet,
  onDownloadPdf,
  onToggleLock,
  onAddYield,
  yieldCount = 0,
  onGeneratePackingSlip,
  generatingPackingSlip = false,
  questionnaireOptions,
  currentQuestionnaireId,
  onSelectQuestionnaire,
  onPreviewCutsheet,
  isQuestionnaireResponse = false,
}: SplitSlotProps) {
  const producerName = useProducerDisplayName();
  const buyerUser = existingBuyer?.contactUser;
  const weightChip =
    hangingWeight > 0 ? (
      <Chip
        label={`${isHangingWeightCalculated ? '~' : ''}${hangingWeight} lbs`}
        size="small"
        variant="outlined"
        color="primary"
      />
    ) : null;
  const titleNode = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography variant="subtitle1" fontWeight={600}>
        {label ?? 'Whole'}
      </Typography>
      {weightChip}
    </Stack>
  );

  const reminderSent = !!reminderSentTo;
  const [showDone, setShowDone] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingAction, setPendingAction] = useState<
    'reassign' | 'unassign' | null
  >(null);

  const handleSendReminder = useCallback(async () => {
    try {
      await onSendReminder();
      setShowDone(true);
      setTimeout(() => {
        setShowDone(false);
      }, 1000);
    } catch {
      // error handled by Apollo
    }
  }, [onSendReminder]);

  // --- Header: label + buyer name (stacked) with overflow menu ---
  const overflowMenu = (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="More actions"
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setPendingAction('reassign');
          }}
        >
          <ListItemIcon>
            <SwapHoriz fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reassign buyer</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setPendingAction('unassign');
          }}
        >
          <ListItemIcon>
            <PersonOff fontSize="small" />
          </ListItemIcon>
          <ListItemText>Unassign split</ListItemText>
        </MenuItem>
        {(onAddYield || onGeneratePackingSlip) && <Divider />}
        {onAddYield && cutsheetName && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onAddYield();
            }}
          >
            <ListItemIcon>
              <Scale fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {yieldCount > 0 ? `Edit yield (${yieldCount})` : 'Add yield'}
            </ListItemText>
          </MenuItem>
        )}
        {onGeneratePackingSlip && (
          <MenuItem
            disabled={generatingPackingSlip}
            onClick={() => {
              setMenuAnchor(null);
              onGeneratePackingSlip();
            }}
          >
            <ListItemIcon>
              {generatingPackingSlip ? (
                <CircularProgress size={16} />
              ) : (
                <ReceiptLong fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {generatingPackingSlip ? 'Generating…' : 'Packing slip'}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
      {pendingAction && (
        <ConfirmDestructiveDialog
          title={
            pendingAction === 'reassign' ? 'Reassign buyer?' : 'Unassign split?'
          }
          body={`${
            pendingAction === 'reassign' ? 'Reassigning' : 'Unassigning'
          } will clear the current buyer, cutsheet, and any saved responses on this split. This cannot be undone.`}
          confirmLabel={pendingAction === 'reassign' ? 'Reassign' : 'Unassign'}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => {
            const action = pendingAction;
            setPendingAction(null);
            if (action === 'reassign') onChangeBuyer();
            else onLeaveUnassigned();
          }}
        />
      )}
    </>
  );

  if (buyerUser) {
    const displayName = isSelf
      ? `Myself · ${producerName}`
      : getBuyerDisplayName(buyerUser);

    // --- Status bars ---
    const showWarning =
      !isLocked &&
      !isSelf &&
      !cutsheetName &&
      canSendReminder &&
      (reminderSent || showDone);
    const showError = !cutsheetName && !isLocked && !showWarning;

    // --- Cutsheet action buttons ---
    let resendIcon: React.ReactNode = <Send />;
    if (sendingReminder) resendIcon = <CircularProgress size={16} />;
    else if (showDone) resendIcon = <Check />;

    let cutsheetActions: React.ReactNode;

    if (isLocked && cutsheetName) {
      cutsheetActions = (
        <LockedCutsheetActions
          downloadingPdf={downloadingPdf}
          onDownloadPdf={onDownloadPdf}
        />
      );
    } else if (cutsheetName) {
      cutsheetActions = (
        <UnlockedCutsheetActions
          downloadingPdf={downloadingPdf}
          onFillCutsheet={onFillCutsheet}
          onEditCutsheet={onEditCutsheet}
          onDownloadPdf={onDownloadPdf}
        />
      );
    } else if (showWarning) {
      cutsheetActions = (
        <ReminderActions
          sendingReminder={sendingReminder}
          showDone={showDone}
          resendIcon={resendIcon}
          onSendReminder={handleSendReminder}
          onFillCutsheet={onFillCutsheet}
        />
      );
    } else {
      cutsheetActions = (
        <MissingCutsheetActions
          isSelf={isSelf}
          canSendReminder={canSendReminder}
          sendingReminder={sendingReminder}
          onFillCutsheet={onFillCutsheet}
          onSendReminder={handleSendReminder}
        />
      );
    }

    const lockButton = isProcessor && onToggleLock && (
      <Tooltip title={isLocked ? 'Unlock split' : 'Lock split'}>
        <span>
          <IconButton
            size="small"
            onClick={onToggleLock}
            disabled={togglingLock}
            aria-label={isLocked ? 'Unlock split' : 'Lock split'}
          >
            {togglingLock ? (
              <CircularProgress size={16} />
            ) : isLocked ? (
              <Lock fontSize="small" />
            ) : (
              <LockOpen fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );

    const headerAction =
      lockButton || !isLocked ? (
        <Stack direction="row" alignItems="center" gap={0.5}>
          {!isLocked && overflowMenu}
        </Stack>
      ) : undefined;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <CardHeader
          title={titleNode}
          subheader={displayName}
          action={headerAction}
          avatar={lockButton}
          sx={{ p: 0 }}
          slotProps={{
            title: { variant: 'subtitle1', fontWeight: 600 },
            subheader: { variant: 'body2', color: 'text.secondary' },
          }}
        />

        {/* Warning bar: request sent, awaiting buyer */}
        {showWarning && <WarningBar reminderSentTo={reminderSentTo} />}

        {/* Locked bar: processor locked the split */}
        {isLocked && (
          <LockedBar
            processorName={processorName}
            cutsheetName={cutsheetName}
          />
        )}

        {/* Error bar: cutsheet not filled in */}
        {showError && <ErrorBar message="Cutsheet not filled in" />}

        {/* Success bar: cutsheet assigned (not shown when locked — locked bar replaces it) */}
        {cutsheetName && !isLocked && (
          <SuccessBar
            cutsheetName={cutsheetName}
            isResponse={isQuestionnaireResponse}
            onPreview={onPreviewCutsheet}
          />
        )}

        {/* Questionnaire selector */}
        {questionnaireOptions &&
          questionnaireOptions.length > 0 &&
          !isLocked &&
          !cutsheetName &&
          onSelectQuestionnaire && (
            <FormControl size="small" sx={{ mt: 1.5, width: '100%' }}>
              <InputLabel>Cutsheet Form</InputLabel>
              <Select
                label="Cutsheet Form"
                value={currentQuestionnaireId ?? ''}
                onChange={(e) => onSelectQuestionnaire(e.target.value || null)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {questionnaireOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        {/* Action buttons */}
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ mt: 'auto', pt: 1.5 }}
        >
          {cutsheetActions}
        </Stack>
      </Box>
    );
  }

  // --- Unassigned slot ---
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        {titleNode}
        <Typography variant="body2" sx={{ visibility: 'hidden' }}>
          &nbsp;
        </Typography>
      </Box>

      {/* Error bar: buyer not assigned */}
      <ErrorBar message="Buyer not assigned" />

      <>
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Who&apos;s getting this
            {label ? ` ${label.toLowerCase().replace(/\s*\d+$/, '')}` : ''}?
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Person />}
            disabled={disabled || loading}
            onClick={onSelectMyself}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            {myselfLabel}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Search />}
            disabled={disabled || loading}
            onClick={onSelectExisting}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            Existing
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAdd />}
            disabled={disabled || loading}
            onClick={onSelectNew}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            New
          </Button>
        </Stack>
      </>
    </Box>
  );
}
